// client/src/hooks/useBanners.js
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export const useBanners = () => {
    const [banners, setBanners] = useState([]);
    const [options, setOptions] = useState({ 
        tipos: [],
        dimensions: {} 
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchBannerOptions = useCallback(async () => {
        try {
            const [tiposResponse, dimensionsResponse] = await Promise.all([
                api.get('/admin/institucional/banners/options'),
                api.get('/admin/institucional/banners/dimensions')
            ]);
            
            setOptions({
                tipos: tiposResponse.data.data.tipos || [],
                dimensions: dimensionsResponse.data.data || {}
            });
        } catch (err) {
            console.error('Erro ao buscar opções de banner:', err);
            setOptions({
                tipos: ['horizontal', 'quadrado', 'vertical', 'left-image-content', 'right-image-content'],
                dimensions: {}
            });
        }
    }, []);

    const fetchBanners = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/admin/institucional/banners');
            const sortedBanners = response.data.data.sort((a, b) => a.pedido - b.pedido);
            setBanners(sortedBanners);
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao buscar banners');
        } finally {
            setLoading(false);
        }
    }, []);

    const getBanner = useCallback(async (id) => {
        try {
            const response = await api.get(`/admin/institucional/banners/${id}`);
            return response.data;
        } catch (err) {
            throw new Error(err.response?.data?.error || 'Erro ao buscar o banner');
        }
    }, []);

    const createBanner = useCallback(async (bannerData) => {
        try {
            const response = await api.post('/admin/institucional/banners', bannerData);
            setBanners(prev => [...prev, response.data.data].sort((a, b) => a.pedido - b.pedido));
            return response.data;
        } catch (err) {
            throw new Error(err.response?.data?.error || 'Erro ao criar banner');
        }
    }, []);

    const updateBanner = useCallback(async (id, bannerData) => {
        try {
            const response = await api.put(`/admin/institucional/banners/${id}`, bannerData);
            setBanners(prev => prev.map(b => b._id === id ? response.data.data : b).sort((a, b) => a.pedido - b.pedido));
            return response.data;
        } catch (err) {
            throw new Error(err.response?.data?.error || 'Erro ao atualizar banner');
        }
    }, []);

    const deleteBanner = useCallback(async (id) => {
        try {
            await api.delete(`/admin/institucional/banners/${id}`);
            setBanners(prev => prev.filter(banner => banner._id !== id));
        } catch (err) {
            throw new Error(err.response?.data?.error || 'Erro ao deletar banner');
        }
    }, []);

    const uploadBannerMedia = useCallback(async (id, deviceType, file) => {
        try {
            const formData = new FormData();
            // uploadImagemMiddleware (server/middlewares/upload.js) espera o
            // campo 'imagem' — 'file' causava "Unexpected field" no multer e
            // quebrava o upload silenciosamente antes de chegar no controller.
            formData.append('imagem', file);

            // A instância `api` tem 'Content-Type': 'application/json' como
            // default — se não sobrescrevermos aqui, o axios entende que é
            // JSON e serializa o FormData com JSON.stringify (perdendo o
            // arquivo de verdade). Só fixar 'multipart/form-data' também é
            // errado: sem o parâmetro boundary (que só o navegador sabe
            // gerar a partir do FormData real), o multer não consegue
            // parsear o corpo e req.file chega undefined no controller. O
            // valor `undefined` remove o header da requisição por completo,
            // deixando o navegador detectar o FormData e montar o
            // Content-Type com boundary sozinho.
            const response = await api.put(`/admin/institucional/banners/${id}/upload/${deviceType}`, formData, {
                headers: { 'Content-Type': undefined }
            });

            setBanners(prev => prev.map(b => b._id === id ? response.data.data : b));
            return response.data;
        } catch (err) {
            throw new Error(err.response?.data?.error || `Erro no upload da imagem (${deviceType})`);
        }
    }, []);

    // Gera uma imagem por IA (Gemini) — não salva em nenhum banner nem sobe
    // pro S3, só devolve o base64 pro chamador tratar como um arquivo
    // "escolhido" (mesmo fluxo do upload manual, que só vira upload de
    // verdade quando o formulário é salvo).
    const generateBannerImage = useCallback(async (prompt, tipo) => {
        try {
            const response = await api.post('/admin/institucional/banners/generate', { prompt, tipo });
            return response.data.data; // { image (base64), mimeType, width, height, avisos }
        } catch (err) {
            throw new Error(err.response?.data?.error || 'Erro ao gerar imagem com IA');
        }
    }, []);

    const updateBannersOrder = useCallback(async (orderedBanners) => {
        try {
            const bannersToUpdate = orderedBanners.map((banner, index) => ({ _id: banner._id, order: index }));
            await api.put('/admin/institucional/banners/update-order', { banners: bannersToUpdate });
            setBanners(orderedBanners.map((banner, index) => ({...banner, pedido: index})));
        } catch (err) {
            fetchBanners();
            throw new Error(err.response?.data?.error || 'Erro ao atualizar ordem dos banners');
        }
    }, [fetchBanners]);

    useEffect(() => {
        fetchBanners();
        fetchBannerOptions();
    }, [fetchBanners, fetchBannerOptions]);

    return {
        banners,
        setBanners,
        options,
        loading,
        error,
        getBanner,
        createBanner,
        updateBanner,
        deleteBanner,
        uploadBannerMedia,
        generateBannerImage,
        updateBannersOrder,
        refetch: fetchBanners
    };
};